// Pet Tag Studio main thread (spec §6). The DOCUMENT (src/doc.js) is the state: every control
// dispatches a change, the worker lays the document out, and the page draws the last layout the
// worker accepted. No geometry runs here.
//   simple mode   — a template owns the positions (layout.custom = false); no dragging
//   advanced mode — select, drag (trackpad-style on touch), snap, nudge, size, rotate, add, delete.
//   faces         — `face` is the face being edited. A back element's x is a BACK-VIEW x and the back view is
//                   drawn as seen from the back, so every tool works on either face with the same code.
//   share link    — `#s=…` is read on load and on hashchange (src/share.js); a bad link changes nothing.
//                   All gesture/snap/hit/step LOGIC is pure code in src/editor/*; this file only
//                   wires DOM events to it.
import '@3dp/ui/shell.css';
import '@3dp/ui/picker.css';   // the font control and its sheet (unify-ui ship 4a): before the tool's own stylesheet
import '@3dp/ui/colours.css';  // the colour control (unify-ui ship 4b): before the tool's own stylesheet too
import './main.css';
import { publishBarHeight, publishHeaderHeight } from '@3dp/ui/bar-height';
import { pageLanguage } from '@3dp/ui/locales';
import { asset } from '@3dp/ui/asset';
import { $, el } from './ui/dom.js';
import { holdRepeat } from './ui/hold-repeat.js';
import { createWorkerClient } from './worker-client.js';
import { SHAPE_TABLE } from './shape-table.js';
import { defaultDoc, applyChange, newTextElement, newIconElement, nextElementId,
  SCALE_MIN, SCALE_MAX, MAX_ELEMENTS } from './doc.js';
import { ICON_IDS } from './icons.js';
const roleLabel = (e) => (e.type === 'icon' ? S.roleLabel.icon : (S.roleLabel[e.id] ?? S.roleLabel.text));
import { iconSvg, shapeSvg } from './ui/glyphs.js';
import { PRESETS, applyPreset, presetOf } from './presets.js';
import { createHistory, record, undo, redo, canUndo, canRedo } from './editor/history.js';
import { createSession, propose, settle, downloadState } from './editor/session.js';
import { latestWins } from './editor/latest.js';
import { viewToSvg, guidesToSvg, svgToModel, engraveShade } from './editor/canvas2d.js';
import { snapMove } from './editor/snap.js';
import { hitsAt, tapSelection } from './editor/hit.js';
import { wireShare } from './ui/share-ui.js';
import { shareUrl } from './share.js';             // ship 3 (R21): the switch's links carry the design
import { wireLangSwitch } from '@3dp/ui/switch';
import { mountLangOffer } from '@3dp/ui/offer';
import { startGesture, moveGesture, dragPosition, nudge, rotateStep, NUDGE_MM, NUDGE_BIG_MM } from './editor/gesture.js';
import { hit } from '@3dp/core/stats';
import { recordOf, asListed, askedFor, offerFor, megabytes, DEFAULT_FONT, listMayHelp, builtFontId } from '@3dp/core/fonts/choose';
import { curatedEntry, fontKey } from '@3dp/core/fonts/catalog';
import { PICKER_CORE } from '@3dp/core/fonts/picker-core';
import { createFontControl } from '@3dp/ui/font-control';
import { createColours } from '@3dp/ui/colours';
import { COLOUR_CORE, slotName } from '@3dp/core/filaments';
import { printsTwoColours } from './slots.js';
import { cleanText, stripFormat } from './sanitize.js';
import { S } from './strings.js';
import { worded } from './worded.js';                 // ship 3 (R9): the worker's codes in the page's words

// D9: the colour actually shown for the front face — textColor when raised, else the tag colour
// itself (an engraved doc "is" the tag colour; preview3d.js darkens/shades it via z, canvas2d via
// engraveShade). Passing this as BOTH preview3d.setColors args when engraved makes every vertex the
// same colour, which is exactly "everything is tag colour" (D9).
const effectiveTextColor = (doc) => (doc.frontMode === 'raised' ? doc.textColor : doc.color);
// Preview3d fix: the hole's reinforcing rim (front face, raised above z = T) is part of the PLATE
// (filament 1), not the text — its vertices must stay tag-coloured even where z > T. The rim's
// centre + radius are in the MODEL frame (session.shown.view is always the FRONT view, sx = 1 —
// src/layout2d.js viewOf), the same frame the mesh's own vertex positions are in before preview3d's
// display-only rotation, so no extra transform is needed here.
function previewHole() {
  const h = session.shown?.view?.hole;
  return h ? { x: h.x, y: h.y, rimR: h.rimR } : null;
}
const PLACEHOLDER = S.placeholder;
const MESH_DELAY = 150;        // ms after the last accepted layout before the 3D view rebuilds
const TEXT_DEBOUNCE = 400;     // ms after the last keystroke before the layout is requested
const TEXT_COMMIT = 1000;      // ms pause that ends a typing gesture (one undo step)

hit('pet-tag', 'open');
hit('pet-tag', 'choice', `lang:${pageLanguage().id}`); // R25: the page's OWN address's language, never the browser's

// ---- state ----
let session = createSession(defaultDoc('circle'));

// ship 3 (R21): the language switch and the English-page offer. A chosen font's RECORD travels in
// the link; an uploaded font FILE never would — the note says so while the menu is open.
wireLangSwitch(window, { linkFor: (href) => shareUrl(href, session.doc), loses: () => session.doc.font.src === 'upload' });
mountLangOffer(window, { linkFor: (href) => shareUrl(href, session.doc) });   // the offer's link carries the design too (B1)
let history = createHistory(session.doc);
let selected = null;           // element id highlighted in the 2D view
let uiMode = 'simple';         // 'advanced' after "ปรับเอง"; a customised document is ALWAYS advanced
let gesture = null;            // pointer gesture in progress on the 2D view (src/editor/gesture.js)
let drag = null;               // { id, base: doc before the drag, start: {x,y}, pos: {x,y}, guides }
let lastPointerType = 'touch';
// Tracked on the whole document, not just the canvas: the first selection is often made on a CHIP,
// and the hint must name the input actually in use (mouse → direct drag, touch → trackpad-style).
document.addEventListener('pointerdown', (e) => { lastPointerType = e.pointerType === 'mouse' ? 'mouse' : 'touch'; }, true);
const advanced = () => uiMode === 'advanced' || session.doc.layout.custom;
let face = 'front';            // the face being edited: 'front' | 'back'
const shownView = () => (face === 'back' ? session.shown.backView : session.shown.view);
let viewMode = '2d';
let exporting = false;
let exportError = null;        // shown until the next change

// ---- worker (src/worker-client.js) ----
const client = createWorkerClient({ url: window.__WORKER_URL__, recycleAfter: 300,
  onFatal: (message) => { setStatus(message); setDownloads(false, S.loadFailed); } });
const recycleWorkerIfDue = client.recycleIfDue;
// Every request has a clock (unify-ui ruling R22): 10 s on the worker's own work, counted from its `started` — never
// while a font file is on its way (worker-client.js). The engine cannot be interrupted, so when the clock fires the
// worker is ended and a new one started, and the page says so; the next edit goes to the new worker. A request that
// was waiting in the worker that was ended is told the same.
const BUILD_TIMEOUT_MS = 10000;
let buildTimeoutMs = BUILD_TIMEOUT_MS;      // the clock in force: a test may shorten it through window.__PET_TAG__ (the last lines of this file)
const TIMED_OUT = { code: 'timeout', message: S.timedOut };
// A downloaded family's file on its way (unify-ui ship 2): the status line says which and how far.
const familyOf = (font) => (curatedEntry(font, fontList) || {}).family || font.id;   // a Google family's id IS its name
let fontProgress = null;       // the last progress of a font download: { id, received, total } (for the tests)
const mb = (bytes) => (bytes / 1048576).toFixed(1);
const progressSeen = new WeakSet();   // the designs whose font file's line was brought into view
function onFontProgress(doc, p) {
  fontProgress = { id: doc.font.id, received: p.received, total: p.total };
  // the line of a font file on its way is in the preview, and the font list is cards below it on a phone: the preview is
  // brought into view once, when the line first shows (unify-ui ruling R65)
  if (doc.font === session.doc.font && !progressSeen.has(doc)) { progressSeen.add(doc); $('#stage').scrollIntoView({ block: 'nearest' }); }
  if (doc.font === session.doc.font) setStatus(S.fontLoading(familyOf(doc.font), mb(p.received), p.total > 0 ? mb(p.total) : 0));
}
async function call(msg) {
  const r = await client.call(msg, { limitMs: buildTimeoutMs, onProgress: (p) => onFontProgress(msg.doc, p) });
  const code = r.ok === false && r.error ? r.error.code : null;
  if (code === 'timeout') { client.replace(); return { type: r.type, ok: false, error: TIMED_OUT }; }
  if (code === 'replaced') return { type: r.type, ok: false, error: TIMED_OUT };
  return worded(r, S);                                            // the worker speaks codes (R9): its reply gains the page's words here, once
}

// ---- requests: one in flight per kind, latest wins ----
// A layout is asked for through requestLayout(): it first tells the worker the font of the design as it stands (`pick`),
// so that a family still on its way for an older choice is abandoned at once (ruling R46) — the layout in flight for it
// comes back `stale`, and the pump below then sends the newest design.
function requestLayout(doc) { client.tell({ type: 'pick', font: doc.font }); sendLayout(doc); }
const sendLayout = latestWins(
  (doc) => call({ type: 'layout', doc }),
  (doc, reply) => {
    // A downloaded family that could not be had (unify-ui acceptance 11): the page stays usable. The design goes back to
    // the font of the layout on show (Kanit when there is none), and the notice names the family — unless the visitor
    // has already picked another font, which then simply lays out.
    // A family neither list holds (ruling R100): the worker built the design in Kanit and says so — the notice, and the default.
    if (reply.ok && reply.fellBack && session.doc.font === doc.font) { notOffered(doc.font); return; }
    if (!reply.ok && reply.error.code === 'font-load' && doc.font.src === 'google') {
      linkNote(S.fontLinkNote(familyOf(doc.font)));
      fonts.markFailed(fontKey(doc.font));                        // its row in the sheet says so at the next open (as name-tag's)
      if (session.doc.font === doc.font) change({ type: 'font', font: session.shown ? session.shown.doc.font : DEFAULT_FONT }, { commit: false });
      else { setStatus(''); requestLayout(session.doc); }
      $('#link-note').scrollIntoView({ block: 'nearest' });       // the line that says why is brought into view: on a phone the font list is cards below it (after the rebuild is asked for: nothing thrown here may stop it)
      return;
    }
    session = settle(session, doc, reply); drawStage(); syncControls(); scheduleMesh(); runSettled();
  },
  recycleWorkerIfDue);

// Taking the layout over (move / size / rotate / add → layout.custom = true) FREEZES the positions
// in the document. While a template layout is still on its way those positions are stale (a line
// typed 300 ms ago still sits at its default 0,0) — so such a change waits for the reply first.
// A refused layout (session.error) does not block: the document then still carries the last
// accepted placements, which is the best starting point there is.
let afterLayout = [];
const settledForCustom = () => session.doc.layout.custom || session.doc === session.shown?.doc || session.error !== null;
function whenSettled(fn) {
  if (settledForCustom()) { fn(); return; }
  afterLayout.push(fn);
  clearTimeout(textTimer);
  requestLayout(session.doc);
}
function runSettled() {
  if (afterLayout.length === 0 || !settledForCustom()) return;
  const queue = afterLayout; afterLayout = [];
  for (const fn of queue) fn();
}

let meshTimer = null, meshDoc = null, meshHole = null, preview = null, previewPromise = null;
const requestMesh = latestWins(
  (doc) => call({ type: 'mesh', doc }).then(r => { client.countGeneration(); return r; }),
  (doc, reply) => {
    if (doc !== meshDoc || viewMode !== '3d') return;            // stale, or the user went back to 2D
    if (!reply.ok) { setStatus(reply.error.message); setBadge(''); return; }
    preview.setMesh(reply.mesh);
    // Bug: setMesh() colourises the NEW geometry with whatever tagColor/textColor/threshold/hole
    // preview3d's closure last held — which could be from a syncControls() call for a LATER doc
    // (colour edits keep firing while this mesh was still building). Re-apply colours from meshDoc
    // — the exact doc this mesh was built from — so the z = thickness threshold and the hole's rim
    // mask always match the geometry actually being shown, never a doc the mesh hasn't caught up to.
    preview.setColors(meshDoc.color, effectiveTextColor(meshDoc), meshDoc.thickness, meshHole);
    setStatus('');
    setBadge(S.triangles(reply.numTri));
  },
  recycleWorkerIfDue);
function scheduleMesh(delay = MESH_DELAY) {
  clearTimeout(meshTimer);
  if (viewMode !== '3d' || !session.shown || !preview) return;
  if (session.shown.doc === meshDoc) return;                     // the 3D view already shows this layout
  setStatus(S.building);
  // meshHole is captured in the SAME instant as meshDoc (session.shown.doc === meshDoc right here),
  // so it always names the hole that belongs to meshDoc, never a later session.shown.
  meshTimer = setTimeout(() => { meshDoc = session.shown.doc; meshHole = previewHole(); requestMesh(meshDoc); }, delay);
}

// ---- changes ----
// commit: true = this change completes a gesture → one undo step now.
function change(c, { commit = true } = {}) {
  exportError = null;
  session = propose(session, applyChange(session.doc, c));
  if (commit) commitGesture();
  syncControls();
  refreshDownloads();
  requestLayout(session.doc);
}
function commitGesture() { history = record(history, session.doc); syncUndo(); }
const commitSettled = () => whenSettled(commitGesture);          // after a change that itself waited for the layout (FIFO)
function travel(step) {
  history = step(history);
  exportError = null;
  session = propose(session, history.present);
  if (!session.doc[face].some(e => e.id === selected)) selected = null;
  buildFields();
  syncControls(); syncUndo(); refreshDownloads();
  requestLayout(session.doc);
}

// ---- stage: 2D view, issues, chips ----
const setStatus = (t) => { const n = $('#status'); n.textContent = t; n.classList.toggle('hidden', !t); };
// The notice above the stage (the element a broken link's message uses; its × closes it).
const linkNote = (t) => { $('#link-note-text').textContent = t; $('#link-note').classList.remove('hidden'); };
const setBadge = (t) => { const n = $('#badge'); n.textContent = t; n.classList.toggle('hidden', !t); };

// Beside a report of characters the font lacks: ONE family that has them, in one tap, with its size (ruling R14) — by
// the script of the missing characters, and only while the font in use is a bundled one (one offer, no chain: after
// the download the line names what is still missing, and the list stays for a choice by hand). Nothing is asked of any
// host before the tap.
function offerFont(li, chars) {
  const entry = chars && chars.length ? offerFor(chars, fontList, session.doc.font, pageLanguage().id) : null;   // ship 3: a ja / zh page offers its own family first
  if (!entry && chars && listMayHelp(chars, session.doc.font)) li.append(el('span', { className: 'ui-offer-note', textContent: S.pickFromList }));   // R83
  if (!entry) return;
  const b = el('button', { type: 'button', className: 'ui-offer', textContent: S.useFont(entry.family, megabytes(entry.bytes)) });
  b.dataset.font = entry.id;
  b.onclick = () => change({ type: 'font', font: recordOf(entry) });
  li.append(b);
}

function drawStage() {
  const shown = session.shown;
  if (shown) {
    const bad = new Set(shown.issues.filter(i => i.elId && i.face === face).map(i => i.elId));   // ids are unique per FACE only
    const d = session.doc;
    // D9: the back is ALWAYS engraved (spec §3) — an engrave shade there regardless of frontMode;
    // the front is textColor when raised, the same engrave shade when engraved. engraveShade (not
    // shadeHex directly) so a dark tag (Black, Filasophy Ink, …) lightens toward white instead of
    // darkening into itself — a plain shadeHex there gave contrast ≈ 1, invisible text.
    const text = face === 'back' ? engraveShade(d.color) : (d.frontMode === 'raised' ? d.textColor : engraveShade(d.color));
    $('#view2d').innerHTML = viewToSvg(shownView(), { fill: d.color, text, bad, selected });
    drawDrag();                                                  // a reply that lands mid-drag must not snap the element back
    if (viewMode === '2d') setStatus('');
  }
  const list = $('#issues');
  list.replaceChildren();
  if (session.error) {
    const li = el('li', { className: 'error', textContent: session.error.message });
    const action = session.error.action;                           // the way out as ONE tap (src/templates.js PHONE_TO_BACK)
    if (action) {
      const b = el('button', { type: 'button', id: 'btn-way-out', className: 'ghost-btn', textContent: action.label });
      b.onclick = () => { const { label, ...c } = action; uiMode = 'simple'; selected = null; change(c); buildFields(); };
      li.append(b);
    }
    if (session.error.code === 'missing-chars') offerFont(li, session.error.chars);
    list.append(li);
  }
  else if (shown) {
    // advanced mode keeps the stage short (the sheet must stay reachable under it): first issue + a count
    const all = shown.issues, show = advanced() && all.length > 1 ? all.slice(0, 1) : all;
    // one offer for everything the font lacks on the tag, on the first such line that is on show
    const lacking = [...new Set(all.filter(i => i.code === 'missing-chars').flatMap(i => i.chars))];
    let offered = false;
    for (const i of show) {
      const more = all.length - show.length;
      const li = Object.assign(el('li', { textContent: more > 0 ? `${i.message} · ${S.more(more)}` : i.message }), { className: i.code === 'empty' ? 'hint' : 'error' });
      if (i.code === 'missing-chars' && !offered) { offerFont(li, lacking); offered = true; }
      list.append(li);
    }
  }
  drawChips();
  refreshDownloads();
}
function drawChips() {
  const row = $('#chips');
  row.replaceChildren();
  for (const e of session.doc[face]) {
    const what = e.type === 'icon' ? (S.icon[e.iconId] || '') : (e.text.trim() || S.emptyEl);
    const b = el('button', { type: 'button', className: 'chip' + (e.id === selected ? ' on' : ''), textContent: `${roleLabel(e)}: ${what}` });
    b.dataset.el = e.id;
    b.onclick = () => {
      if (selected === e.id) { select(null); document.activeElement?.blur(); }   // blur: a focused field re-selects its element
      else select(e.id);                                            // chips are advanced-mode only (M5/U2)
    };
    row.append(b);
  }
  const add = el('button', { type: 'button', id: 'btn-add', className: 'chip add', textContent: '＋', title: S.addTitle });
  add.setAttribute('aria-label', S.addTitle);
  add.disabled = session.doc[face].length >= MAX_ELEMENTS;
  add.onclick = () => $('#add-menu').classList.toggle('hidden');
  row.append(add);
}

// ---- faces ----
function setFace(f) {
  if (f === face) return;
  face = f; selected = null; gesture = null; drag = null;
  $('#add-menu').classList.add('hidden');
  preview?.setFace(face);
  drawStage(); syncControls();
}
$('#face-front').onclick = () => setFace('front');
$('#face-back').onclick = () => setFace('back');

// ---- selection, modes ----
function select(id) {
  selected = id;
  if (id && advanced()) showHint();
  drawStage(); syncControls();
}
function syncMode() {
  const adv = advanced(), custom = session.doc.layout.custom;
  document.body.classList.toggle('adv', adv);
  $('#btn-advanced').classList.toggle('hidden', adv);
  $('#btn-simple').classList.toggle('hidden', !adv || custom);
  $('#btn-reset-template').classList.toggle('hidden', !custom);
  $('#sheet').classList.toggle('hidden', !adv);
  if (!adv) $('#add-menu').classList.add('hidden');
  // the canvas owns the touch ONLY while something is selected in advanced mode (else the page scrolls)
  $('#view2d').classList.toggle('grab', adv && selected !== null);
}
const RESET_WARNING = S.resetWarning;
let confirmYes = null;
function askConfirm(message, onYes) {
  $('#confirm-msg').textContent = message;
  $('#confirm').classList.remove('hidden');
  confirmYes = onYes;
  $('#confirm').scrollIntoView({ block: 'nearest' });
}
function closeConfirm() { $('#confirm').classList.add('hidden'); confirmYes = null; }
$('#confirm-yes').onclick = () => { const f = confirmYes; closeConfirm(); f?.(); };
$('#confirm-no').onclick = closeConfirm;
// Entering advanced mode starts with NOTHING selected: the page still scrolls over the canvas, and the
// sheet says how to select. (A focused simple-mode field had selected its element.)
// M5/U4: the ปรับเอง button sits in the LAST card in simple mode; advanced mode moves that card first (CSS order),
// so bring the stage back into view — the canvas is what the user is about to work on, the sheet right under it.
$('#btn-advanced').onclick = () => {
  uiMode = 'advanced'; $('#g-adv').open = true; document.activeElement?.blur(); select(null);
  $('#stage').scrollIntoView({ block: 'nearest' });
};
$('#btn-simple').onclick = () => { uiMode = 'simple'; select(null); };
$('#btn-reset-template').onclick = () => askConfirm(RESET_WARNING, () => { uiMode = 'simple'; selected = null; change({ type: 'reset-template' }); buildFields(); });

// One-line hint on the first selection (spec §11): the trackpad-style drag is unusual.
const HINT_KEY = 'pettag.hint.drag';
const HINT = S.hint;
const hintSeen = () => { try { return localStorage.getItem(HINT_KEY) === '1'; } catch { return false; } };
function showHint() {
  if (hintSeen()) return;
  $('#hint-text').textContent = lastPointerType === 'mouse' ? HINT.mouse : HINT.touch;
  $('#hint').classList.remove('hidden');
}
function dismissHint() {
  $('#hint').classList.add('hidden');
  try { localStorage.setItem(HINT_KEY, '1'); } catch { /* private mode: the hint simply shows again next time */ }
}
$('#hint-close').onclick = dismissHint;

// ---- pointer gestures on the 2D view ----
const view2d = $('#view2d');
function viewPoint(e) {
  const svg = view2d.querySelector('svg');
  const m = svg && svg.getScreenCTM();
  if (!m) return null;
  const p = new DOMPoint(e.clientX, e.clientY).matrixTransform(m.inverse());
  return { model: svgToModel([p.x, p.y]), mmPerPx: 1 / m.a };
}
function drawDrag() {
  if (!drag || !session.shown) return;
  const shownEl = session.shown.doc[face].find(e => e.id === drag.id);
  const path = view2d.querySelector(`path.el[data-el="${CSS.escape(drag.id)}"]`);
  // the group is inside the y-flip, so the offset is in MODEL millimetres
  if (shownEl && path) path.setAttribute('transform', `translate(${drag.pos.x - shownEl.x} ${drag.pos.y - shownEl.y})`);
  const g = view2d.querySelector('g.guides');
  if (g) g.innerHTML = guidesToSvg(drag.guides, shownView());
}
view2d.addEventListener('pointerdown', (e) => {
  if (e.button > 0 || gesture || !session.shown) return;
  const vp = viewPoint(e);
  if (!vp) return;
  lastPointerType = e.pointerType === 'mouse' ? 'mouse' : 'touch';
  const hits = hitsAt(shownView(), vp.model, vp.mmPerPx);
  gesture = { ...startGesture({ x: e.clientX, y: e.clientY, pointerType: e.pointerType, hits, selected, canDrag: advanced() && settledForCustom() }),
    pointerId: e.pointerId, mmPerPx: vp.mmPerPx };
  try { view2d.setPointerCapture(e.pointerId); } catch { /* a synthetic pointer has no capture — the gesture still works while it stays on the canvas */ }
});
view2d.addEventListener('pointermove', (e) => {
  if (!gesture || e.pointerId !== gesture.pointerId) return;
  gesture = { ...gesture, ...moveGesture(gesture, { x: e.clientX, y: e.clientY }) };
  if (!gesture.dragging) return;
  if (!drag) {                                                   // the drag starts now
    const target = session.doc[face].find(x => x.id === gesture.target);
    if (!target) return;
    drag = { id: target.id, base: session.doc, start: { x: target.x, y: target.y }, pos: { x: target.x, y: target.y }, guides: [] };
    if (selected !== target.id) selected = target.id;            // a mouse may grab an unselected element
    view2d.classList.add('dragging');
  }
  if (!session.doc[face].some(x => x.id === drag.id)) {           // Ctrl+Z mid-drag removed the element — end the drag
    drag = null; view2d.classList.remove('dragging'); return;
  }
  const others = session.doc[face].filter(x => x.id !== drag.id).map(x => ({ x: x.x, y: x.y }));
  const snapped = snapMove(dragPosition(gesture, drag.start, gesture.mmPerPx), others, shownView().centre);
  drag.pos = { x: snapped.x, y: snapped.y }; drag.guides = snapped.guides;
  drawDrag();                                                    // the element follows the finger by transform only…
  change({ type: 'move', face, id: drag.id, x: snapped.x, y: snapped.y }, { commit: false });   // …validation catches up (latest wins)
});
function endGesture(e, cancelled) {
  if (!gesture || e.pointerId !== gesture.pointerId) return;
  const g = gesture; gesture = null;
  view2d.classList.remove('dragging');
  if (drag) {
    const d = drag; drag = null;
    if (cancelled) { session = propose(session, d.base); syncControls(); requestLayout(session.doc); }   // the browser took the touch back
    else { commitGesture(); dismissHint(); }                     // ONE undo step per drag
    drawStage(); syncControls();
    return;
  }
  if (!cancelled) select(tapSelection(g.hits, selected));
}
view2d.addEventListener('pointerup', (e) => endGesture(e, false));
view2d.addEventListener('pointercancel', (e) => endGesture(e, true));

// ---- downloads ----
function setDownloads(enabled, note = '') {
  $('#btn-3mf').disabled = !enabled;
  $('#btn-stl').disabled = !enabled;
  const n = $('#dl-note');
  n.textContent = note; n.className = ''; n.onclick = null;
}
// A finishing export restores the LATEST verdict — never a blanket "enabled".
function refreshDownloads() {
  if (exporting) return;
  if (exportError) { setDownloads(false, exportError); return; }
  if (session.doc !== session.shown?.doc && !session.error) { setDownloads(false, S.layingOut); return; }   // layout pending
  const s = downloadState(session);
  setDownloads(s.ok, s.note);
}
function download(bytes, filename, mime) {
  const blob = new Blob([bytes], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = el('a', { href: url, download: filename });
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 4000);
}
async function doExport(format) {
  const doc = session.shown.doc;                                 // exactly what the canvas shows
  exporting = true;
  setDownloads(false, S.makingFile);
  try {
    const r = await call({ type: 'export', doc, format });
    client.countGeneration();
    if (!r.ok) { exportError = r.error.message; return; }
    download(r.bytes, r.filename, r.mime);
    // Choices are sent once per download (spec D14): they measure what actually gets printed.
    // colours: 'two' only when the print actually carries a second filament (D8: raised and the two colours
    // another filament each — slots.js, R99: an own colour counts as its nearest filament, so the beacon says
    // what the FILE carries); preset: presetOf(doc) — a PURE check of the doc actually being exported, never a
    // stale flag (final review: a stateful lastPresetId kept reporting a preset after the doc had
    // since diverged from it, e.g. a manual colour change).
    const twoColour = printsTwoColours(doc);
    const preset = presetOf(doc);
    hit('pet-tag', 'download', format);
    hit('pet-tag', 'choice', `shape:${doc.shape}`);
    hit('pet-tag', 'choice', `template:${doc.layout.template}`);
    // the id — a slug, or a family name — of the font the worker BUILT the file in: a name out of a link that no list holds
    // is never sent (the worker built it in Kanit and said so, `fellBack`); the registry's pattern takes every catalog family
    hit('pet-tag', 'choice', `font:${builtFontId(r, doc.font, DEFAULT_FONT)}`);
    hit('pet-tag', 'choice', `mode:${doc.frontMode}`);
    hit('pet-tag', 'choice', `colours:${twoColour ? 'two' : 'one'}`);
    if (preset) hit('pet-tag', 'choice', `preset:${preset}`);
  } finally {
    exporting = false;
    refreshDownloads();
    recycleWorkerIfDue();
  }
}
$('#btn-3mf').onclick = () => doExport('3mf');
$('#btn-stl').onclick = () => doExport('stl');

// ---- controls (built once; syncControls() makes them show the document — needed for undo) ----
function chipRow(sel, entries, onPick) {
  const row = $(sel);
  for (const [id, label] of entries) {
    const b = el('button', { type: 'button', className: 'chip', textContent: label });
    b.dataset.v = id;
    b.onclick = () => onPick(id);
    row.append(b);
  }
}
// D6: shape/icon chip FACES are inline SVG silhouettes (src/ui/glyphs.js), never emoji — a label
// span carries the Thai name so the chip still reads (and toContainText() e2e checks still pass).
function svgChip(sel, entries, onPick, idPrefix) {
  const row = $(sel);
  for (const [id, svg, label] of entries) {
    const b = el('button', { type: 'button', className: 'chip svg-chip' });
    if (idPrefix) b.id = `${idPrefix}-${id}`;
    b.innerHTML = `<span class="chip-glyph">${svg}</span><span class="chip-label">${label}</span>`;
    b.dataset.v = id; b.setAttribute('aria-label', label); b.title = label;
    b.onclick = () => onPick(id);
    row.append(b);
  }
  return row;
}
svgChip('#shapes', Object.entries(SHAPE_TABLE).map(([id]) => [id, shapeSvg(id), S.shape[id]]), (shape) => {
  change({ type: 'shape', shape });                              // may switch to the shape's own default template → other fields, other faces
  if (!session.doc[face].some(e => e.id === selected)) selected = null;
  buildFields(); syncControls();
});
chipRow('#templates', Object.entries(S.template), (template) => {
  const apply = () => { uiMode = 'simple'; selected = null; change({ type: 'template', template }); buildFields(); };
  if (session.doc.layout.custom) askConfirm(RESET_WARNING, apply); else apply();
});
for (const style of ['inside', 'tab']) $(`#hole-${style}`).onclick = () => change({ type: 'hole', style });
for (const m of ['raised', 'engraved']) $(`#mode-${m}`).onclick = () => change({ type: 'frontMode', frontMode: m });

// ---- pet presets (D10): one tap sets shape + template + colours (and the icon, where the template
// has one), keeps whatever name/phone was typed, and returns to simple mode — ONE undo step. src/presets.js
// is pure; this is the page's part: the RESET_WARNING confirm for a custom layout and the commit. The
// D14 download beacon no longer tracks "the last preset applied" as page state — presetOf(doc), a pure
// function of whatever doc is actually being exported, replaces it (see doExport above).
svgChip('#presets', PRESETS.map(p => [p.id, shapeSvg(p.shape), S.preset[p.id]]), (id) => {
  const apply = () => {
    uiMode = 'simple'; selected = null;
    exportError = null;
    session = propose(session, applyPreset(session.doc, id));
    commitGesture();                                               // ONE undo step for the whole preset
    buildFields(); syncControls(); drawChips(); refreshDownloads();
    requestLayout(session.doc);
  };
  if (session.doc.layout.custom) askConfirm(RESET_WARNING, apply); else apply();
}, 'preset');

// Typing: the text goes into the document at once (nothing typed is lost), the layout follows
// TEXT_DEBOUNCE after the last keystroke, one undo step per pause / blur.
let textTimer = null, commitTimer = null;
function wireTextInput(input, target) {                          // target() → { face, id } of the element this input edits
  // Format characters never stay (ruling R47). A paste that holds one is cleaned BEFORE the field's length limit counts
  // it — else a character nobody can see would push a visible one out; whatever else brings one (typing, a drop,
  // autofill) is cleaned in the field as it arrives, the caret kept where it was.
  input.addEventListener('paste', (ev) => {
    const raw = ev.clipboardData ? ev.clipboardData.getData('text') : '';
    if (!raw || stripFormat(raw) === raw) return;                  // nothing to clean: the browser's own paste
    ev.preventDefault();
    const a = input.selectionStart ?? input.value.length, b = input.selectionEnd ?? a;
    const room = input.maxLength > 0 ? Math.max(0, input.maxLength - (input.value.length - (b - a))) : Infinity;
    input.setRangeText(stripFormat(raw).replace(/[\r\n]/g, '').slice(0, room), a, b, 'end');
    input.dispatchEvent(new Event('input', { bubbles: true }));
  });
  input.addEventListener('input', () => {
    if (stripFormat(input.value) !== input.value) {                // format characters only: the field is never recomposed as it is typed (B3)
      const at = stripFormat(input.value.slice(0, input.selectionStart ?? input.value.length)).length;
      input.value = stripFormat(input.value); input.setSelectionRange(at, at);
    }
    session = propose(session, applyChange(session.doc, { type: 'text', ...target(), text: input.value }));
    exportError = null;
    refreshDownloads(); drawChips();
    clearTimeout(textTimer); textTimer = setTimeout(() => requestLayout(session.doc), TEXT_DEBOUNCE);
    clearTimeout(commitTimer); commitTimer = setTimeout(commitGesture, TEXT_COMMIT);
  });
  input.addEventListener('blur', () => { clearTimeout(commitTimer); commitGesture(); });
}
function iconPicker(current, onPick, idPrefix) {
  const row = el('div', { className: 'chip-row icon-pick' });
  for (const iconId of ICON_IDS) {
    const b = el('button', { type: 'button', id: `${idPrefix}-${iconId}`, className: 'chip svg-chip' + (iconId === current ? ' on' : '') });
    b.innerHTML = `<span class="chip-glyph">${iconSvg(iconId)}</span><span class="chip-label">${S.icon[iconId]}</span>`;
    b.title = S.icon[iconId];
    b.setAttribute('aria-label', S.icon[iconId]); b.dataset.v = iconId;
    b.onclick = () => onPick(iconId);
    row.append(b);
  }
  return row;
}

// Simple mode: one row per element — its text (or icon) and its size slider (el.scale, 40–100%) — for BOTH
// faces: someone filling in a form does not think in faces. Ids are unique per face only, so a back
// element whose id also exists on the front gets the DOM key `back-<id>`.
const fieldEntries = (d = session.doc) => [
  ...d.front.map(e => ({ f: 'front', e, key: e.id })),
  ...d.back.map(e => ({ f: 'back', e, key: d.front.some(x => x.id === e.id) ? `back-${e.id}` : e.id })),
];
function buildFields() {
  const box = $('#fields');
  box.replaceChildren();
  for (const { f, e, key } of fieldEntries()) {
    const where = S.face[f] ?? '';   // '' on the front face (ruling: no intentionally-empty leaves — the guard test stays mechanical)
    // name-tag rows (M5/U3): label | control | value
    const pct = el('b', { id: `scale-val-${key}` });
    const slider = el('input', { id: `scale-${key}`, type: 'range', min: SCALE_MIN * 100, max: SCALE_MAX * 100, step: 5 });
    slider.addEventListener('input', () => change({ type: 'scale', face: f, id: e.id, scale: slider.value / 100 }, { commit: false }));
    slider.addEventListener('change', commitGesture);
    const size = el('label', { className: 'line-size ui-row' }, el('span', { textContent: e.type === 'icon' ? S.iconSize : S.textSize }), slider, el('output', {}, pct));
    let row;
    if (e.type === 'icon') {
      const picker = iconPicker(e.iconId, (iconId) => { change({ type: 'icon', face: f, id: e.id, iconId }); buildFields(); syncControls(); }, `pick-${key}`);
      row = el('div', { className: 'el-field ui-row' }, el('span', { textContent: S.fieldLabel.icon + where }), picker, size);
    } else {
      const input = el('input', { id: `inp-${key}`, type: e.id === 'phone' ? 'tel' : 'text', maxLength: e.id === 'phone' ? 15 : 20,
        placeholder: Object.hasOwn(PLACEHOLDER, e.id) ? PLACEHOLDER[e.id] : '', autocomplete: 'off' });
      wireTextInput(input, () => ({ face: f, id: e.id }));
      // typing into a field shows the face it is on — the phone number appears on the back while it is typed
      input.addEventListener('focus', () => { setFace(f); if (selected !== e.id) select(e.id); });
      // the title stays a direct <span> child of .el-field (p3.spec reads `.el-field > span`); the input is
      // labelled by it instead of being wrapped in a <label>
      const title = el('span', { id: `lbl-${key}`, textContent: (S.fieldLabel[e.id] ?? S.fieldLabel.text) + where });
      input.setAttribute('aria-labelledby', title.id);
      row = el('div', { className: 'el-field ui-row' }, title, input, size);
    }
    row.dataset.el = key; row.dataset.face = f;
    box.append(row);
  }
}

// ---- advanced mode: the control sheet of the selected element ----
const selectedEl = () => session.doc[face].find(e => e.id === selected) || null;
wireTextInput($('#sheet-text'), () => ({ face, id: selected }));
$('#sheet-em').addEventListener('input', (e) => { const em = +e.target.value; whenSettled(() => { if (selected) change({ type: 'em', face, id: selected, em }, { commit: false }); }); });
$('#sheet-em').addEventListener('change', commitSettled);
$('#btn-delete').onclick = () => { if (!selected) return; const id = selected; selected = null; change({ type: 'delete', face, id }); buildFields(); drawStage(); };

function nudgeSelected(dir, step = NUDGE_MM) {
  whenSettled(() => {
    const e = selectedEl();
    if (!e) return;
    const p = nudge({ x: e.x, y: e.y }, dir, step);
    change({ type: 'move', face, id: e.id, x: p.x, y: p.y }, { commit: false });
  });
}
function rotateSelected(dir, fine) {
  whenSettled(() => {
    const e = selectedEl();
    if (e) change({ type: 'rot', face, id: e.id, rot: rotateStep(e.rot, dir, fine) }, { commit: false });
  });
}
for (const dir of ['left', 'up', 'down', 'right']) holdRepeat($(`#nudge-${dir}`), { tap: () => nudgeSelected(dir), repeat: () => nudgeSelected(dir), onRelease: commitSettled });
holdRepeat($('#btn-rot-ccw'), { tap: () => rotateSelected('ccw', false), repeat: () => rotateSelected('ccw', true), onRelease: commitSettled });
holdRepeat($('#btn-rot-cw'), { tap: () => rotateSelected('cw', false), repeat: () => rotateSelected('cw', true), onRelease: commitSettled });

function syncSheet() {
  const e = advanced() ? selectedEl() : null;
  $('#sheet-empty').classList.toggle('hidden', !!e);
  $('#sheet-body').classList.toggle('hidden', !e);
  if (!e) return;
  $('#sheet-text-row').classList.toggle('hidden', e.type !== 'text');
  $('#sheet-icon-row').classList.toggle('hidden', e.type !== 'icon');
  if (e.type === 'text') {
    $('#sheet-title').textContent = roleLabel(e);
    const input = $('#sheet-text');
    input.type = e.id === 'phone' ? 'tel' : 'text';
    if (cleanText(input.value) !== e.text) input.value = e.text;   // the field as typed stands while it stores as the text (B3)
  } else {
    $('#sheet-icons').replaceChildren(...iconPicker(e.iconId, (iconId) => change({ type: 'icon', face, id: e.id, iconId }), 'sheet-icon').children);
  }
  $('#sheet-em').value = e.em; $('#sheet-em-val').textContent = e.em.toFixed(1);
  $('#rot-val').textContent = e.rot;
}

// [+] → ข้อความ / ไอคอน. A new element lands on the body centre and is selected.
function addElement(make) {
  if (session.doc[face].length >= MAX_ELEMENTS) return;
  const [cx, cy] = session.shown ? shownView().centre : [0, 0];
  const element = { ...make(), x: Math.round(cx * 100) / 100, y: Math.round(cy * 100) / 100 };
  $('#add-menu').classList.add('hidden');
  uiMode = 'advanced';
  whenSettled(() => {
    selected = element.id;
    change({ type: 'add', face, element });
    drawStage();
    if (element.type === 'text') $('#sheet-text').focus({ preventScroll: true });
  });
}
$('#add-text').onclick = () => addElement(() => newTextElement(nextElementId(session.doc, face, 'text')));
$('#add-icons').replaceChildren(...iconPicker(null, (iconId) => addElement(() => newIconElement(nextElementId(session.doc, face, 'icon'), iconId)), 'add-icon').children);

// ---- desktop keyboard: arrows nudge (Shift = 1mm), Ctrl/⌘+Z undo, Ctrl/⌘+Shift+Z redo ----
const ARROW = { ArrowLeft: 'left', ArrowRight: 'right', ArrowUp: 'up', ArrowDown: 'down' };
const typingTarget = (t) => t instanceof HTMLInputElement || t instanceof HTMLSelectElement || t instanceof HTMLTextAreaElement;
document.addEventListener('keydown', (e) => {
  if (typingTarget(e.target)) return;                            // fields and sliders keep their own keys
  if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'z') { e.preventDefault(); travel(e.shiftKey ? redo : undo); return; }
  const dir = ARROW[e.key];
  if (dir && advanced() && selectedEl()) { e.preventDefault(); nudgeSelected(dir, e.shiftKey ? NUDGE_BIG_MM : NUDGE_MM); }
});
document.addEventListener('keyup', (e) => { if (ARROW[e.key] && !typingTarget(e.target)) commitSettled(); });

// The font control (unify-ui ship 4a — @3dp/ui/font-control): the button, the weight select, the line for a family not yet
// print-tested (I4), and the sheet behind the button — the 29 recommended families, then the whole Google catalog (R91).
// fonts/curated.json is the file the worker reads too. A record that IS an entry of the list is held as the entry's own
// (R95, asListed). A design whose font this page cannot build in is shown, and built, in Kanit: silently for an unknown
// bundled id, as always; with a notice that names what was asked for — an uploaded font at once, a family the catalog
// does not hold when the worker says it built in Kanit instead (its reply's `fellBack`, ruling R100).
let fontList = [];             // the list's entries, once fonts/curated.json has loaded
// the document's font is one this page builds in, or it becomes the default — not an undo step: the visitor did nothing
function acceptDocFont() {
  if (!fontList.length) return;
  const asked = session.doc.font;
  if (asked.src === 'google' || curatedEntry(asked, fontList)) {
    const held = asListed(asked, fontList);
    if (fontKey(held) !== fontKey(asked)) change({ type: 'font', font: held }, { commit: false });   // a list entry, named by another record
    return;
  }
  change({ type: 'font', font: DEFAULT_FONT }, { commit: false });
  if (asked.src !== 'bundled') linkNote(S.fontNotOffered(askedFor(asked)));
}
// the worker built the design in Kanit: the family is in neither list (R100) — the notice it shows today, and the default
function notOffered(asked) {
  change({ type: 'font', font: DEFAULT_FONT }, { commit: false });
  if (asked.src !== 'bundled') linkNote(S.fontNotOffered(askedFor(asked)));   // an unknown bundled id: silently, as always (acceptDocFont)
}
// every text the tag prints, front and back (scrutiny B2): the sheet's language chip and its order read all of them
const printedText = (d) => [...d.front, ...d.back].filter((e) => e.type === 'text').map((e) => cleanText(e.text).trim()).filter(Boolean).join(' ');
const fonts = createFontControl({ root: $('#font-pick'), S, core: PICKER_CORE, getFont: () => session.doc.font, getText: () => printedText(session.doc),
  onPick: (f) => change({ type: 'font', font: asListed(recordOf(f), fontList) }),
  onWeight: (font) => change({ type: 'font', font }) });
fetch(asset('fonts/curated.json')).then(r => r.json()).then(list => {
  fontList = list;
  fonts.setList(list);
  acceptDocFont();
}).catch(() => { /* the worker falls back to kanit */ });

$('#rng-size').oninput = (e) => change({ type: 'size', size: +e.target.value }, { commit: false });
$('#rng-size').onchange = commitGesture;
$('#rng-thick').oninput = (e) => change({ type: 'thickness', thickness: +e.target.value }, { commit: false });
$('#rng-thick').onchange = commitGesture;

// The two colours (unify-ui ship 4b: @3dp/ui/colours, D3 — name-tag's control): the tag's and the text's, each a row with
// its swatch and the colour's name under it; a tap opens every filament and brand colour plus the visitor's own (R89;
// spec D7's "never a custom hex" is amended by D3 + R89, I9). The control hands an upper-case #RRGGBB (doc.js HEX_RE).
// An own colour is stored and shown; for the file it counts as its nearest filament (slots.js). Pet-tag has no palettes
// (its presets set the shape and the template too: they stay presets). While the own colour is dragged the change is not
// a step of the undo history; letting go of it is (as the sliders).
const colours = createColours({ root: $('#colours'), S, core: COLOUR_CORE, looksLabel: '',
  onChange: (key, hex, { live }) => change({ type: key, [key]: hex }, { commit: !live }), onLook: () => {} });

// D8's single-nozzle note under the text-colour row: what the 3MF actually carries. The pause height
// names the FIRST LAYER printed entirely in the second filament: layer 1 spans Z 0–0.2 (top 0.2), layer
// k spans (k-1)×0.2–k×0.2 (top k×0.2) — the raised text starts exactly at Z = T (src/assemble.js RAISE_H
// join), so its first full layer is the one whose TOP is T + 0.2, i.e. N = T/0.2 + 1. A single-nozzle
// pause is a Z-height swap, blind to which 3MF PART a triangle belongs to — the hole's reinforcing rim
// is geometrically raised above Z = T too (D9/preview3d.js), so even though the 3MF assigns it to
// filament 1 (the plate) as its own part, a manual pause-and-swap prints it in the SECOND filament
// along with the text. Final review 2026-09-25: the note used to omit both the layer number and this
// rim caveat.
function colourNote(d) {
  if (d.frontMode === 'engraved') return S.engravedNote;
  if (!printsTwoColours(d)) return S.singleColour;                  // one filament (an own colour counts as its nearest: R99)
  const tagName = slotName(d.color), textName = slotName(d.textColor);   // what to load: a filament's or brand colour's name, an own colour's nearest
  const z = (d.thickness + 0.2).toFixed(1);
  const n = Math.round(d.thickness / 0.2) + 1;
  return S.threeMf(tagName, textName, z, n);
}

$('#btn-undo').onclick = () => travel(undo);
$('#btn-redo').onclick = () => travel(redo);
function syncUndo() { $('#btn-undo').disabled = !canUndo(history); $('#btn-redo').disabled = !canRedo(history); }

const markOn = (sel, v) => document.querySelectorAll(sel).forEach(b => b.classList.toggle('on', b.dataset.v === v));
function syncControls() {
  const d = session.doc, def = SHAPE_TABLE[d.shape];
  markOn('#shapes .chip', d.shape);
  markOn('#templates .chip', d.layout.template);
  markOn('#hole-seg button', d.hole.style);
  markOn('#mode-seg button', d.frontMode);
  const engraved = d.frontMode === 'engraved';      // the text's colour is off when engraved (the letters are the tag's colour, D9)
  colours.set([{ key: 'color', label: S.colourLabel.tag, value: d.color }, { key: 'textColor', label: S.colourLabel.text, value: d.textColor, off: engraved ? 'disabled' : undefined }]);
  $('#colour-note').textContent = colourNote(d);
  // Recolour immediately so a colour-swatch tap feels instant — but the z = thickness threshold
  // must match whatever mesh is ACTUALLY showing (meshDoc), not the live doc being edited: a
  // thickness/shape/hole change fires syncControls long before the rebuilt mesh (scheduleMesh's
  // debounce + worker round trip) arrives, and using d.thickness here would recolour the OLD mesh
  // with a threshold that belongs to a mesh that doesn't exist yet (the reply handler above then
  // corrects it for good once the new mesh lands).
  if (preview) preview.setColors(d.color, effectiveTextColor(d), meshDoc ? meshDoc.thickness : d.thickness, previewHole());
  const rng = $('#rng-size');
  rng.min = def.minSize; rng.max = def.maxSize; rng.value = d.size; $('#size-val').textContent = d.size;
  $('#rng-thick').value = d.thickness; $('#thick-val').textContent = d.thickness.toFixed(1);
  fonts.setFont();
  for (const { f, e, key } of fieldEntries(d)) {
    const row = document.querySelector(`#fields .el-field[data-el="${CSS.escape(key)}"]`);
    if (!row) continue;
    const input = $(`#inp-${key}`);
    if (input && cleanText(input.value) !== e.text) input.value = e.text;   // undo/redo; while typing the field stores as the text (it is not recomposed: B3)
    $(`#scale-${key}`).value = Math.round(e.scale * 100);
    $(`#scale-val-${key}`).textContent = Math.round(e.scale * 100) + '%';
    row.classList.toggle('sel', f === face && e.id === selected);
  }
  markOn('#face-toggle button', face);
  $('#face-note').classList.toggle('hidden', face !== 'back');
  syncMode();
  syncSheet();
}

// ---- 2D / 3D ----
async function setViewMode(mode) {
  viewMode = mode;
  $('#view-2d').classList.toggle('on', mode === '2d');
  $('#view-3d').classList.toggle('on', mode === '3d');
  $('#view2d').classList.toggle('hidden', mode !== '2d');
  $('#preview').classList.toggle('hidden', mode !== '3d');
  if (mode === '2d') { clearTimeout(meshTimer); setBadge(''); setStatus(session.shown ? '' : S.loading); return; }
  if (!preview) {
    setStatus(S.loading3d);
    // three.js loads only when 3D is first opened. Two rapid switches to 3D can both pass the
    // !preview check before the first import resolves → two previews on the same container, so
    // the creation is memoized: the second call awaits the first.
    if (!previewPromise) {
      previewPromise = import('./preview3d.js').then(({ createPreview }) => {
        preview = createPreview($('#preview'), session.doc.color, effectiveTextColor(session.doc));
        preview.setFace(face);
      });
      // the memo clears on a failed import OR a failed creation (WebGLRenderer throws when no
      // WebGL context can be created) — the await below still rejects to the caller, next tap retries
      previewPromise.catch(() => { previewPromise = null; });
    }
    try {
      await previewPromise;
    } catch {                                                      // WebGL denied / import failed — back to 2D, no recursive setViewMode
      viewMode = '2d';
      $('#view-2d').classList.add('on'); $('#view-3d').classList.remove('on');
      $('#view2d').classList.remove('hidden'); $('#preview').classList.add('hidden');
      setStatus(S.load3dFailed);
      return;
    }
  }
  // Same meshDoc-thickness rule as syncControls: switching back to 3D recolours whatever mesh is
  // still showing (from before the 2D detour) before scheduleMesh(0) below has a chance to rebuild
  // it — session.doc.thickness could already differ from the mesh actually on screen.
  preview.setColors(session.doc.color, effectiveTextColor(session.doc), meshDoc ? meshDoc.thickness : session.doc.thickness, previewHole());
  scheduleMesh(0);
  if (session.shown && session.shown.doc === meshDoc) setStatus('');
}
$('#view-2d').onclick = () => setViewMode('2d');
$('#view-3d').onclick = () => setViewMode('3d');

// ---- share link + LINE banner (src/ui/share-ui.js). The link carries what was ASKED FOR (session.doc), valid or not.
// A good link becomes ONE undo step, so the work it replaced is one ↶ away; a bad link never reaches this callback.
const loadFromHash = wireShare({
  getDoc: () => session.doc,
  onShare: () => hit('pet-tag', 'share'),
  onDoc: (doc) => {
    clearTimeout(textTimer); clearTimeout(commitTimer);
    commitGesture();                                               // typing that has not reached the history yet (no blur happens on a hashchange)
    exportError = null; selected = null; gesture = null; drag = null;
    uiMode = doc.layout.custom ? 'advanced' : 'simple';
    session = propose(session, doc);
    commitGesture();
    if (face !== 'front') { face = 'front'; preview?.setFace(face); }
    buildFields(); syncControls(); drawChips(); refreshDownloads();
    acceptDocFont();                                               // a link may name a font this page does not offer
    requestLayout(session.doc);
  },
});

// The fixed download bar covers the bottom of the page: tell the page how much (--ui-bar-h; main.css pads for it, shell.css ends the panel above it).
publishBarHeight($('#downloads'));
publishHeaderHeight(document.querySelector('header.ui-header')); // --ui-header-h: ship 3's taller header row; the desktop panel leaves its room too

// ---- start ----
buildFields();
syncControls();
syncUndo();
drawChips();
loadFromHash();
client.whenReady().then(() => { window.__APP_READY__ = true; requestLayout(session.doc); });
// Read-only window for the browser tests (and for debugging on a phone): the documents, never the means to change them.
// meshDoc/previewState (final review finding 6): the doc the CURRENTLY DISPLAYED 3D mesh was built
// from, and the colour state preview3d.js is actually rendering with — lets an e2e test assert the
// z = thickness threshold matches the shown mesh without reading rendered pixels.
// `buildTimeoutMs` is the one value a test may change (read when a request is sent): the clock of every request.
window.__PET_TAG__ = { get buildTimeoutMs() { return buildTimeoutMs; }, set buildTimeoutMs(ms) { buildTimeoutMs = ms; }, fontProgress: () => fontProgress, doc: () => session.doc, shown: () => session.shown?.doc ?? null, selected: () => selected, face: () => face,
  meshDoc: () => meshDoc, previewState: () => preview?.debugState?.() ?? null };
